import type { Dispatch, PropsWithChildren, ReactElement } from 'react';
import { useReducer, createContext, useContext, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { GET_SESSION, type GetSessionData, type GetSessionVariables } from './graphql/getSession';
import { LOCAL_STORAGE_CLIENT_UUID_KEY } from '../config/constants';
import { v4 as uuidv4 } from 'uuid';
import localStore from '../lib/storage/localStore';
import type { SessionState, SessionStateAction } from './reducers/sessionReducer';
import sessionReducer from './reducers/sessionReducer';

export const defaultState: SessionState = {
  session: undefined,
  clientUuid: null,
};

type SessionProviderCtx = [SessionState, Dispatch<SessionStateAction>];

const SessionCtx = createContext<SessionProviderCtx>([defaultState, () => null]);

const SessionProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [state, dispatch] = useReducer(sessionReducer, defaultState);
  const { eventUniqueCode = '' } = useParams();

  const { data } = useQuery<GetSessionData, GetSessionVariables>(GET_SESSION, {
    fetchPolicy: 'cache-and-network',
    variables: {
      eventUniqueCode,
    },
  });

  useEffect(() => {
    if (data?.getSession) {
      dispatch({ type: 'setSession', payload: data.getSession });
    }
    localStore
      .getItem(LOCAL_STORAGE_CLIENT_UUID_KEY)
      .then((uuid) => {
        if (!uuid) {
          const newUuid = data?.getSession?.uuid || uuidv4();
          localStore.setItem(LOCAL_STORAGE_CLIENT_UUID_KEY, newUuid).then(() => {
            console.info(`CLIENT UUID: ${newUuid}`);
            dispatch({ type: 'setClientUuid', payload: newUuid });
          });
          return;
        }
        dispatch({ type: 'setClientUuid', payload: uuid as string });
        console.info(`CLIENT UUID: ${uuid}`);
      })
      .catch((error) => {
        console.error('Error getting client uuid', error);
      });
  }, [data?.getSession, dispatch]);

  return <SessionCtx.Provider value={[state, dispatch]}>{children}</SessionCtx.Provider>;
};

export const useSession = (): SessionProviderCtx => useContext<SessionProviderCtx>(SessionCtx);

export default SessionProvider;
